import { useState } from 'react';
import { 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  IconButton,
  Paper,
  Typography,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Box,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Download, InsertDriveFile, ExpandMore, Archive } from '@mui/icons-material';
import { downloadFile, downloadAllAsZip } from '../../api/jobs';
import { ModelType } from '@/types/colabfold';

interface FileListProps {
  jobId: string;
  modelType: ModelType;
  files: string[];
  jobName: string;
  collapsible?: boolean;
}

interface FileGroup {
  ranked: string[];
  metrics: string[];
  other: string[];
}

export const FileList = ({ jobId, modelType, files, jobName, collapsible = false }: FileListProps) => {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isZipping, setIsZipping] = useState(false);
  const [downloadAttempts, setDownloadAttempts] = useState<Record<string, number>>({});

  const groupedFiles: FileGroup = {
    ranked: files.filter(f => f.includes('rank_')),
    metrics: files.filter(f => 
      f.endsWith('.json') || 
      (modelType === 'proteinopt' && f.endsWith('.csv'))
    ),
    other: files.filter(f => 
      !f.includes('rank_') && 
      !f.includes('relaxed_') && 
      !f.includes('unrelaxed_') && 
      !f.endsWith('.json') &&
      !(modelType === 'proteinopt' && f.endsWith('.csv'))
    )
  };

  const getPrefixedFilename = (filename: string) => {
    // Create a sanitized version of the job name for the filename
    const sanitizedJobName = jobName.replace(/[^a-zA-Z0-9]/g, '_');
    return `${sanitizedJobName}_${filename}`;
  };

  const handleDownload = async (filename: string) => {
    try {
      setDownloading(filename);
      
      // Track download attempts
      const attempts = (downloadAttempts[filename] || 0) + 1;
      setDownloadAttempts(prev => ({ ...prev, [filename]: attempts }));
      
      // If this is a retry, show a message
      if (attempts > 1) {
        setError(`Retrying download (attempt ${attempts})...`);
      }
      
      await downloadFile(modelType, jobId, filename, getPrefixedFilename(filename));
      
      // Clear error if successful
      setError(null);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to download file';
      setError(errorMessage);
      console.error('Error downloading file:', error);
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadAll = async () => {
    try {
      setIsZipping(true);
      setError('Preparing ZIP file. This may take a moment...');
      
      await downloadAllAsZip(modelType, jobId, jobName);
      
      // Clear error if successful
      setError(null);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to download zip file';
      setError(errorMessage);
      console.error('Error downloading zip:', error);
    } finally {
      setIsZipping(false);
    }
  };

  const renderFileGroup = (title: string, fileList: string[]) => {
    if (!fileList.length) return null;

    const content = (
      <List>
        {fileList.map((file) => (
          <ListItem
            key={file}
            secondaryAction={
              <IconButton 
                edge="end" 
                onClick={() => handleDownload(file)}
                disabled={downloading === file}
                color={downloadAttempts[file] && downloadAttempts[file] > 1 ? "warning" : "default"}
              >
                {downloading === file ? (
                  <CircularProgress size={24} />
                ) : (
                  <Download />
                )}
              </IconButton>
            }
          >
            <ListItemIcon>
              <InsertDriveFile />
            </ListItemIcon>
            <ListItemText 
              primary={getPrefixedFilename(file)} 
              secondary={downloadAttempts[file] && downloadAttempts[file] > 1 ? 
                `Download attempted ${downloadAttempts[file]} times` : null}
            />
          </ListItem>
        ))}
      </List>
    );

    if (collapsible) {
      return (
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>{title}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {content}
          </AccordionDetails>
        </Accordion>
      );
    }

    return (
      <>
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          {title}
        </Typography>
        {content}
      </>
    );
  };

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Output Files
          </Typography>
          <Tooltip title="Download all files as ZIP">
            <Button
              variant="outlined"
              startIcon={isZipping ? <CircularProgress size={20} /> : <Archive />}
              onClick={handleDownloadAll}
              disabled={isZipping || files.length === 0}
            >
              {isZipping ? 'Preparing ZIP...' : 'Download All'}
            </Button>
          </Tooltip>
        </Box>
        
        {files.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No output files available for this job.
          </Alert>
        ) : (
          <>
            {renderFileGroup('Ranked Models', groupedFiles.ranked)}
            {renderFileGroup('Metrics & Scores', groupedFiles.metrics)}
            {renderFileGroup('Other Files', groupedFiles.other)}
          </>
        )}
      </Paper>

      <Snackbar 
        open={!!error} 
        autoHideDuration={error?.includes('Retrying') || error?.includes('Preparing') ? 2000 : 6000} 
        onClose={() => setError(null)}
      >
        <Alert 
          severity={error?.includes('Retrying') || error?.includes('Preparing') ? "info" : "error"} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}; 